import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { https, settings, clearRedirect401Flag } from "../../utils/config";
import { cacheGet, cacheSet, cacheRemove } from "../../utils/cache";

const initialState = {
  emailUser: null,
  domain_web: null,
  userLogin: null,
  userData: null,
  isLoadLogin: false,
};

export const checkLogin = createAsyncThunk(
  "Login/checkLogin",
  async (data, { rejectWithValue }) => {
    try {
      const response = await https.post("/check-login", data);
      // Trả về toàn bộ data từ API để component và reducer xử lý
      return response.data;
    } catch (error) {
      // Bắt lỗi mạng hoặc lỗi server (500, 4xx)
      if (error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const refreshTokenLogin = createAsyncThunk("Login/refreshTokenLogin", async () => {
  const response = await https.post("/refresh-token");
  // Sửa lại cho nhất quán, trả về toàn bộ data
  return response.data;
});

export const getUserData = createAsyncThunk("Login/getUserData", async () => {
  const response = await https.get("/detail-user");
  return response.data.data;
});
/**
 * LoadDataProduct — cache-aware product fetch.
 *
 * @param {Object} arg
 * @param {string} arg.idw          — warehouse id (required)
 * @param {boolean} [arg.preferCache] — return cached data if valid TTL (default false)
 * @param {boolean} [arg.forceRefresh] — clear cache then fetch fresh (default false)
 *
 * Backward-compatible: LoadDataProduct({ idw }) still works as before.
 *
 * Cache key: `products_${idw}` in sessionStorage, TTL 5 min.
 * Dev-only: logs fetch time (ms) + payload size (KB) to console.
 */
const PRODUCT_CACHE_TTL = 5 * 60 * 1000; // 5 min

export const LoadDataProduct = createAsyncThunk("Login/LoadDataProduct", async (arg) => {
  const idw = arg?.idw || arg;
  const preferCache = arg?.preferCache ?? false;
  const forceRefresh = arg?.forceRefresh ?? false;
  const cacheKey = `products_${idw}`;

  // Force refresh: clear cache first
  if (forceRefresh) {
    cacheRemove(cacheKey);
  }

  // Prefer cache: return immediately if hit
  if (preferCache && !forceRefresh) {
    const cached = cacheGet(cacheKey);
    if (cached) {
      return { status: 200, data: cached, _fromCache: true };
    }
  }

  // Fetch from API
  const t0 = performance.now();
  const response = await https.post("/list-product", { idw });
  const ms = Math.round(performance.now() - t0);

  if (process.env.NODE_ENV !== "production") {
    try {
      const kb = (JSON.stringify(response.data).length / 1024).toFixed(1);
      console.log(`[LoadDataProduct] ${ms}ms | ${kb}KB | idw=${idw}`);
    } catch { /* ignore */ }
  }

  // Cache successful response
  if (response.data?.status === 200 && response.data?.data) {
    cacheSet(cacheKey, response.data.data, PRODUCT_CACHE_TTL);
  }

  return response.data;
});
export const LoadDataCart = createAsyncThunk("Login/LoadDataCart", async (data) => {
  const response = await https.post("/list-cart-v2",data);
  return response.data;
});

export const LoadDataV2 = createAsyncThunk("Login/LoadDataCart", async (data) => {
  // console.log("LoadDataV2",data);
  const response = await https.post("/list-cart-v2",data);
  return response.data;
});
const Login = createSlice({
  name: "Login",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(checkLogin.pending, (state) => {
        state.isLoadLogin = true;
      })
      .addCase(checkLogin.fulfilled, (state, action) => {
        // Dữ liệu thành công được lồng trong { data: { ... } }
        const responseData = action.payload?.data;

        // Chỉ cập nhật state khi đăng nhập thành công
        // user_token: backend Set-Cookie HttpOnly; không set từ FE
        if (responseData?.status === 200 && responseData?.access_token) {
          state.userLogin = responseData;
          settings.setCookie("user_idw", responseData.web_id, 365);
          clearRedirect401Flag();
        }

        state.isLoadLogin = false;
      })
      .addCase(checkLogin.rejected, (state, action) => {
        state.isLoadLogin = false;
        state.error = action.error.message;
      })
      .addCase(getUserData.pending, (state) => {})
      .addCase(getUserData.fulfilled, (state, action) => {
        state.userData = action.payload;
      })
      .addCase(getUserData.rejected, (state, action) => {
        state.error = action.error.message;
      });
  },
});

// export const {} = Login.actions;

export default Login.reducer;
